import { c as _c } from "react/compiler-runtime";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { RAIL_WIDTH, RAIL_WIDTH_CSS_VAR } from "../sidebar/railConstants";
import { MIN_CHAT_PANE_WIDTH } from "../agent-session/chatColumn";
import { useLatestRef } from "@/hooks/useLatestRef";
import { useValueChanged } from "@/hooks/useValueChanged";

/**
 * The chat pane is paid first out of the Codesmith container's width; the
 * side panels may only auto-open with what remains. Priority when space is
 * short: chat, then the left session sidebar, then the right details panel.
 */
export { MIN_CHAT_PANE_WIDTH };

/**
 * App-nav rail width used to estimate the container before first measure.
 * Prefers the live width the mounted rail publishes (48px collapsed, 200px
 * pinned open; see RAIL_WIDTH_CSS_VAR); before the rail mounts, falls back
 * to its collapsed width.
 */
export function estimateAppNavRailWidth(): number {
  const published = Number.parseFloat(document.documentElement.style.getPropertyValue(RAIL_WIDTH_CSS_VAR));
  return Number.isFinite(published) ? published : RAIL_WIDTH;
}
export function canFitLeftSidebar({
  containerWidth,
  leftSidebarWidth
}: {
  containerWidth: number;
  leftSidebarWidth: number;
}): boolean {
  return containerWidth - leftSidebarWidth >= MIN_CHAT_PANE_WIDTH;
}

/**
 * The right panel's budget is what remains after the chat minimum AND the
 * left sidebar's actual footprint (0 when collapsed), so shrinking closes
 * the right panel before it ever squeezes the left one.
 */
export function canFitRightSidebar({
  containerWidth,
  openLeftSidebarWidth,
  rightSidebarWidth
}: {
  containerWidth: number;
  openLeftSidebarWidth: number;
  rightSidebarWidth: number;
}): boolean {
  return containerWidth - openLeftSidebarWidth - rightSidebarWidth >= MIN_CHAT_PANE_WIDTH;
}

/**
 * The left sidebar's footprint for the right panel's budget. The desktop
 * sidebar animates its width closed (see SessionSidebar), so on collapse the
 * footprint outlives the collapsed flag: the width keeps counting until the
 * exit animation finishes (wire `onExitComplete` to the sidebar), keeping
 * the right panel from auto-opening mid-tween and crushing the chat pane
 * below its minimum. Expansion counts the full width immediately, which
 * only errs toward closing the right panel early.
 */
export function useBudgetedLeftSidebarWidth(t0) {
  const $ = _c(5);
  const {
    isCollapsed,
    sidebarWidth
  } = t0;
  const [exitSettled, setExitSettled] = useState(isCollapsed);
  if (useValueChanged(isCollapsed) && !isCollapsed) {
    setExitSettled(false);
  }
  const isCollapsedRef = useLatestRef(isCollapsed);
  let t1;
  if ($[0] !== isCollapsedRef) {
    t1 = () => {
      if (isCollapsedRef.current) {
        setExitSettled(true);
      }
    };
    $[0] = isCollapsedRef;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const onExitComplete = t1;
  const t2 = isCollapsed && exitSettled ? 0 : sidebarWidth;
  let t3;
  if ($[2] !== onExitComplete || $[3] !== t2) {
    t3 = {
      budgetedLeftSidebarWidth: t2,
      onExitComplete
    };
    $[2] = onExitComplete;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/**
 * Live width of the Codesmith container (everything right of the app nav),
 * which is the real estate the chat pane and side panels share. Measured
 * with a ResizeObserver so app-nav expansion and window resizes both count;
 * falls back to a viewport-based estimate until the element is available.
 */
export function useCodesmithContainerWidth(container) {
  const $ = _c(5);
  let t0;
  if ($[0] !== container) {
    t0 = () => container?.getBoundingClientRect().width ?? null;
    $[0] = container;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [measuredWidth, setMeasuredWidth] = useState(t0);
  if (useValueChanged(container) && container) {
    setMeasuredWidth(container.getBoundingClientRect().width);
  }
  let t1;
  let t2;
  if ($[2] !== container) {
    t1 = () => {
      if (!container) {
        return;
      }
      const observer = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width;
        if (width !== undefined) {
          setMeasuredWidth(width);
        }
      });
      observer.observe(container);
      return () => observer.disconnect();
    };
    t2 = [container];
    $[2] = container;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useLayoutEffect(t1, t2);
  if (measuredWidth !== null) {
    return measuredWidth;
  }
  return typeof window === "undefined" ? Number.MAX_SAFE_INTEGER : window.innerWidth - estimateAppNavRailWidth();
}